import React from "react";
import styled from "styled-components";

import { IChargeBasedPricingCalculation } from "../../types/claim";

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const Block = styled.div`
  padding: 20px;
`;

const BlockTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #333;
`;

const BlockIntro = styled.p`
  font-size: 14px;
  color: #555;
  margin: 0 0 16px;
`;

const FormulaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormulaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
`;

const FormulaLabel = styled.div`
  color: #555;

  > span {
    color: #888;
    margin-left: 4px;
  }
`;

const FormulaAmount = styled.div`
  color: #333;
  min-width: 120px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 2px solid #333;
  padding-top: 12px;
  margin-top: 12px;
  font-size: 15px;
  font-weight: 600;
`;

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return "N/A";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function formatMultiplierPercent(multiplier: number): string {
  const pct = multiplier * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

interface IChargeBasedPricingBlockProps {
  calculation: IChargeBasedPricingCalculation;
}

/**
 * Renders the Charge-Based Pricing rule audit when the rule replaces the standard
 * pricing pipeline (no fee schedule, expected = multiplier × charges). Visually
 * distinct from the lesser-of CAP card because there is no "original → capped"
 * frame: this rule IS the pricing, not a ceiling on top of one.
 */
export function ChargeBasedPricingBlock({
  calculation,
}: IChargeBasedPricingBlockProps) {
  const {
    scope,
    multiplier,
    pricedExpectedInCents,
    totalChargeInCents,
    includedChargeInCents,
    perLineCaps,
  } = calculation;

  const multiplierPct = formatMultiplierPercent(multiplier);
  const multiplierDecimal = String(multiplier);

  // For code-filtered TOTAL rules, includedChargeInCents is the rule's denominator
  // (only participating-line charges). For wildcards it equals totalChargeInCents,
  // and we don't need the extra row.
  const showIncludedRow =
    includedChargeInCents !== totalChargeInCents && includedChargeInCents > 0;

  return (
    <>
      <TotalAllowableSection>
        <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
        <TotalAllowableAmount>
          {formatCurrency(pricedExpectedInCents)}
        </TotalAllowableAmount>
      </TotalAllowableSection>

      <Block>
        <BlockTitle>Charge-Based Pricing</BlockTitle>

        {scope === "TOTAL_CHARGES" ? (
          <>
            <BlockIntro>
              This contract prices the claim as a percentage of billed charges.
              No fee schedule, inpatient outlier, or lesser-of cap applies.
            </BlockIntro>
            <FormulaList>
              <FormulaRow>
                <FormulaLabel>Multiplier</FormulaLabel>
                <FormulaAmount>{multiplierPct}</FormulaAmount>
              </FormulaRow>
              <FormulaRow>
                <FormulaLabel>Billed Charges</FormulaLabel>
                <FormulaAmount>
                  {formatCurrency(totalChargeInCents)}
                </FormulaAmount>
              </FormulaRow>
              {showIncludedRow && (
                <FormulaRow>
                  <FormulaLabel>
                    Participating Charges
                    <span>(after Excluded Codes)</span>
                  </FormulaLabel>
                  <FormulaAmount>
                    {formatCurrency(includedChargeInCents)}
                  </FormulaAmount>
                </FormulaRow>
              )}
              <TotalRow>
                <div>
                  Expected ({multiplierDecimal} ×{" "}
                  {formatCurrency(
                    showIncludedRow ? includedChargeInCents : totalChargeInCents
                  )}
                  )
                </div>
                <FormulaAmount>
                  {formatCurrency(pricedExpectedInCents)}
                </FormulaAmount>
              </TotalRow>
            </FormulaList>
          </>
        ) : (
          <FormulaList>
            <FormulaRow>
              <FormulaLabel>Multiplier</FormulaLabel>
              <FormulaAmount>{multiplierPct}</FormulaAmount>
            </FormulaRow>
            {(perLineCaps ?? []).map((line, idx) => {
              // Back-compute the line charge from the stored priced expected
              // so the row reads "{cpt} (multiplier × charge) = expected".
              // The backend only sends pricedExpectedInCents per line — no raw
              // charge — so the division is the only way to render the formula.
              const lineChargeInCents =
                multiplier > 0
                  ? Math.round(line.pricedExpectedInCents / multiplier)
                  : 0;
              return (
                <FormulaRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={`cbp-line-${line.cptCode}-${idx}`}
                >
                  <FormulaLabel>
                    {line.cptCode}
                    <span>
                      ({multiplierDecimal} × {formatCurrency(lineChargeInCents)}
                      )
                    </span>
                  </FormulaLabel>
                  <FormulaAmount>
                    {formatCurrency(line.pricedExpectedInCents)}
                  </FormulaAmount>
                </FormulaRow>
              );
            })}
            <TotalRow>
              <div>Total Expected</div>
              <FormulaAmount>
                {formatCurrency(pricedExpectedInCents)}
              </FormulaAmount>
            </TotalRow>
          </FormulaList>
        )}
      </Block>
    </>
  );
}
